import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  type NotificationItem,
} from '@/lib/api';
import { AppTopbar } from '@/components';
import { BADGE_REFRESH_EVENT } from '@/components/AppShell';
import { friendlyError } from '@/lib/errors';

/**
 * Notifications page — paginated list of every dispatched
 * non-DM notification for the signed-in user. DMs are intentionally
 * excluded (they live on the Messages surface so this page stays
 * the home for ratings / AI / payment / media events).
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // category=other excludes dm.message events; unread reflects
      // the non-DM count (UnreadOtherCount on the backend).
      const res = await notificationsApi.list({ category: 'other' });
      setItems(res.items);
      setUnread(res.unread_other_count);
    } catch (e) {
      setError(friendlyError(e, 'Notifications'));
    }
  }, []);

  useEffect(() => {
    // Auth gate lives in AppShell.
    load();
  }, [load]);

  const onTap = (n: NotificationItem) => {
    if (!n.read_at) {
      setItems((prev) =>
        prev?.map((x) =>
          x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x,
        ) ?? prev,
      );
      setUnread((u) => Math.max(0, u - 1));
      notificationsApi.markRead(n.id)
        .then(() => window.dispatchEvent(new Event(BADGE_REFRESH_EVENT)))
        .catch(() => null);
    }
    if (n.link) navigate(n.link);
  };

  const onMarkAllRead = async () => {
    setItems((prev) =>
      prev?.map((x) =>
        x.read_at ? x : { ...x, read_at: new Date().toISOString() },
      ) ?? prev,
    );
    setUnread(0);
    await notificationsApi.markAllRead().catch(() => null);
    window.dispatchEvent(new Event(BADGE_REFRESH_EVENT));
  };

  return (
    <>
      <AppTopbar
        eyebrow="Notifications"
        right={
          unread > 0 ? (
            <button onClick={onMarkAllRead} style={markAllBtn}>
              Mark all read
            </button>
          ) : undefined
        }
      />
      <main style={{ maxWidth: 720, margin: '0', padding: '0 40px 60px' }}>

      {items === null && !error && <p style={dim}>Loading…</p>}
      {error && (
        <div style={errorBox}>{error}</div>
      )}
      {items && items.length === 0 && (
        <p
          className="serif"
          style={{
            fontSize: 20,
            fontStyle: 'italic',
            fontWeight: 500,
            color: 'var(--ink-3)',
            textAlign: 'center',
            padding: '60px 24px 0',
            margin: 0,
          }}
        >
          No notifications yet.
        </p>
      )}

      {items && items.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((n, i) => (
            <li
              key={n.id}
              onClick={() => onTap(n)}
              style={{
                ...rowStyle,
                borderTop: i === 0 ? `1px solid var(--line)` : 'none',
              }}
            >
              {!n.read_at && <span style={unreadDot} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>
                  {n.title}
                </div>
                <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.45, marginBottom: 6 }}>
                  {n.body}
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      </main>
    </>
  );
}

const markAllBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--line-strong)',
  color: 'var(--coral)',
  borderRadius: 100,
  padding: '8px 16px',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '16px 4px',
  borderBottom: '1px solid var(--line)',
  cursor: 'pointer',
};

const unreadDot: React.CSSProperties = {
  width: 8, height: 8, borderRadius: 4,
  background: 'var(--coral)',
  marginTop: 6,
  flexShrink: 0,
};

const dim: React.CSSProperties = { color: 'var(--ink-3)' };

const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
};
